import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Skeleton,
  Alert,
  AlertTitle,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useColors } from 'frontend/hooks/useColors';

const AccordionErrorState = ({ onRetry, isLoading }) => {
  const { background } = useColors();

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Skeleton variant="text" width="60%" height={30} />
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={100} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Alert
        severity="error"
        icon={<ErrorOutlineIcon fontSize="inherit" />}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          '& .MuiAlert-message': {
            width: '100%',
            textAlign: 'center',
          },
        }}
      >
        <AlertTitle>Error al cargar los datos</AlertTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          No pudimos cargar la información en este momento. Por favor, intente nuevamente más tarde.
        </Typography>
        {onRetry && (
          <Button
            color="inherit"
            size="small"
            onClick={onRetry}
            sx={{
              color: background,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Reintentar
          </Button>
        )}
      </Alert>
    </Box>
  );
};

const CustomAccordion = ({
  id,
  title,
  subtitle,
  summarySubtitle,
  children,
  expanded,
  onChange,
  sx = {},
  icon: Icon,
  isLoading = false,
  hasError = false,
  onRetry,
}) => {
  const { background } = useColors();

  return (
    <Accordion
      id={id}
      expanded={expanded}
      onChange={onChange}
      sx={{
        mb: 2,
        borderRadius: '15px !important',
        '&:first-of-type': {
          borderRadius: '15px !important',
        },
        '&:before': {
          display: 'none',
        },
        ...sx,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: background }} />}
        sx={{
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            m: 0,
          },
          minHeight: expanded ? 64 : 90,
          '& .MuiAccordionSummary-content.Mui-expanded': {
            my: 3,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          {Icon && (
            <Icon
              sx={{
                color: background,
              }}
            />
          )}
          <Typography variant="h5">{title}</Typography>
          {subtitle && !expanded && (
            <Typography variant="body1" color="text.secondary" sx={{ pr: 2, ml: 'auto' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {!subtitle && summarySubtitle && !expanded && (
          <Typography variant="h6" sx={{ pr: 2, ml: 'auto', minWidth: 200, textAlign: 'right' }}>
            {summarySubtitle}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        {isLoading || hasError ? (
          <AccordionErrorState isLoading={isLoading} onRetry={hasError ? onRetry : undefined} />
        ) : (
          children
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default CustomAccordion;
